export type { FilterOption, SelectedFilterValue, FilterSelections } from '@/components/shared';

export interface FilterConfig {
  key: string;
  apiType: string;
  label: string;
  /**
   * May an off-taxonomy value become a CUSTOM entry on this dimension?
   *
   * Drives three behaviours at once, so flipping it is never a UI-only change:
   *   - the custom-value affordances in `FilterDropdown` (the pinned
   *     "+ Add custom value" row and the search-triggered `Add "…"` row)
   *   - the unmatched-label type in `resolveSelections` (custom vs standard)
   *   - the auto-create/warn split in `partitionUnmapped`
   */
  allowCustom: boolean;
  /** Show a "Select all" action in the dropdown footer (long pick-lists). */
  selectAll?: boolean;
}

export type ListType = 'inclusion' | 'exclusion';
export type ListKind = 'accountList' | 'contactList' | 'other';
export type UploadStatus = 'uploading' | 'done' | 'error';

export interface UploadedFile {
  uid: string;
  id?: string;
  name: string;
  fileKey?: string;
  rowCount?: number;
  listType: ListType | null;
  listKind: ListKind | null;
  status: UploadStatus;
  error?: string;
}

export interface IntentTopicSelection {
  topicId: string;
  name: string;
  category: string;
  subcategory: string;
  source: 'search' | 'browse' | 'suggested';
}

/**
 * One technology selected on an audience request.
 *
 * Structurally identical to `@/lib/technographics/types`' TechnographicSelection so a play
 * activated from Intelligence needs no translation — and the same shape the backend persists on its own
 * `technographicSelections` column, separate from intent topics.
 */
export interface TechnographicSelection {
  topicId: string;
  /** Display name; carried on the selection because the lazily-fetched tree is not persisted. */
  label: string;
  /** Category → Group → Type → Product. Anything above a product matches the products beneath. */
  tier: 'category' | 'group' | 'type' | 'product';
  mode: 'runs' | 'notRuns';
}

/**
 * NPJB-763 — the accounts an Intelligence play resolved to, as a Company Domains filter.
 *
 * A hand edit changes `domains` only. `resolvedAt` and `sourceAudienceId` stay, so the
 * field still says where the list came from and Refresh still knows where to read it.
 */
export interface CompanyDomains {
  domains: string[];
  /** ISO time Intelligence resolved the list. `null` for a list typed by hand. */
  resolvedAt: string | null;
  /** The saved Intelligence audience the list came from. `null` when nothing was carried. */
  sourceAudienceId: string | null;
}

export const emptyCompanyDomains = (): CompanyDomains => ({
  domains: [],
  resolvedAt: null,
  sourceAudienceId: null,
});
